import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { checkPaymentStatus } from '../services/mercadoPagoService';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { useTokens } from '../hooks/useTokens';
import { trackEvent } from '../lib/meta-pixel';
import { motion } from 'framer-motion';
import './PaymentResultPage.css';

type PaymentStatus = 'success' | 'failure' | 'pending';

const PaymentResultPage: React.FC = () => {
  const { status } = useParams<{ status: PaymentStatus }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshData } = useTokens();
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  // Derivar status a partir de query params quando a rota não possui :status
  const queryStatusRaw = searchParams.get('status') || searchParams.get('collection_status') || searchParams.get('subscription_status');
  const effectiveStatus: PaymentStatus | undefined = (() => {
    const s = (queryStatusRaw || status || '').toLowerCase();
    if (s === 'approved' || s === 'success' || s === 'authorized') return 'success';
    if (s === 'pending') return 'pending';
    if (s === 'failure' || s === 'rejected' || s === 'cancelled') return 'failure';
    return undefined;
  })();

  useEffect(() => {
    const verifyPaymentStatus = async () => {
      try {
        const paymentId = searchParams.get('payment_id');
        const collectionId = searchParams.get('collection_id');
        const collectionStatus = searchParams.get('collection_status');
        const externalReference = searchParams.get('external_reference');

        if (paymentId) {
          const details = await checkPaymentStatus(paymentId);
          setPaymentDetails(details);
          
          // Se pagamento aprovado, atualizar dados do usuário
          if (details.status === 'approved' && user) {
            // Track purchase
            trackEvent('Purchase', {
              content_name: details.description || 'Assinatura/Tokens',
              content_ids: [paymentId],
              value: details.transactionAmount,
              currency: 'BRL'
            });

            // User profile will be automatically updated in the hybrid system
            await refreshData();
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status do pagamento:', error);
      } finally {
        setLoading(false);
      }
    };

    verifyPaymentStatus();
  }, [searchParams, user, refreshData]);

  const getStatusConfig = () => {
    switch (effectiveStatus) {
      case 'success':
        return {
          icon: '✅',
          title: 'Pagamento Aprovado!',
          message: 'Seus tokens foram adicionados à sua conta com sucesso.',
          description: 'Agora você pode usar seus tokens para acessar cursos, softwares e recursos premium.',
          buttonText: 'Ver Meus Tokens',
          buttonAction: () => navigate('/tokens'),
          className: 'success'
        };
      case 'failure':
        return {
          icon: '❌',
          title: 'Pagamento Rejeitado',
          message: 'Não foi possível processar seu pagamento.',
          description: 'Verifique os dados do seu cartão e tente novamente.',
          buttonText: 'Tentar Novamente',
          buttonAction: () => navigate('/tokens'),
          className: 'failure'
        };
      case 'pending':
        return {
          icon: '⏳',
          title: 'Pagamento Pendente',
          message: 'Seu pagamento está sendo processado.',
          description: 'Seus tokens serão adicionados assim que o pagamento for confirmado.',
          buttonText: 'Verificar Status',
          buttonAction: () => window.location.reload(),
          className: 'pending'
        };
      default:
        return {
          icon: '❓',
          title: 'Status Desconhecido',
          message: 'Não foi possível determinar o status do pagamento.',
          description: 'Entre em contato com o suporte se o problema persistir.',
          buttonText: 'Voltar ao Início',
          buttonAction: () => navigate('/'),
          className: 'unknown'
        };
    }
  };

  const config = getStatusConfig();

  if (loading) {
    return (
      <div className="payment-result-page">
        <div className="payment-result-container">
          <div className="loading-spinner-large"></div>
          <h2>Verificando pagamento...</h2>
          <p>Aguarde enquanto confirmamos seu pagamento.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-result-page">
      <div className={`payment-result-container ${config.className}`}>
        <div className="result-icon">
          {config.icon}
        </div>
        
        <h1>{config.title}</h1>
        <p className="result-message">{config.message}</p>
        <p className="result-description">{config.description}</p>
        
        {paymentDetails && (
          <div className="payment-details">
            <h3>Detalhes do Pagamento</h3>
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">ID do Pagamento:</span>
                <span className="detail-value">{paymentDetails.id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className={`detail-value status-${paymentDetails.status}`}>
                  {paymentDetails.status === 'approved' ? 'Aprovado' :
                   paymentDetails.status === 'pending' ? 'Pendente' :
                   paymentDetails.status === 'rejected' ? 'Rejeitado' : 'Cancelado'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Valor:</span>
                <span className="detail-value">R$ {paymentDetails.transactionAmount?.toFixed(2)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Método:</span>
                <span className="detail-value">{paymentDetails.paymentMethod}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Data:</span>
                <span className="detail-value">
                  {new Date(paymentDetails.dateCreated).toLocaleString('pt-BR')}
                </span>
              </div>
            </div>
          </div>
        )}
        
        <div className="result-actions">
          <button 
            className="btn-primary"
            onClick={config.buttonAction}
          >
            {config.buttonText}
          </button>
          
          {status === 'failure' && (
            <button 
              className="btn-secondary"
              onClick={() => navigate('/support')}
            >
              Contatar Suporte
            </button>
          )}
        </div>
        
        <div className="result-footer">
          <p>
            Precisa de ajuda? <a href="/support">Entre em contato conosco</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentResultPage;